import {
  Mail,
  MessageCircle,
  Phone,
  FileText,
  Database,
  Bot,
  type LucideIcon,
} from "lucide-react";
import { PreOrderSourceType } from "@/app/api/types/preOrder";
import type { PreOrder } from "@/app/api/types/preOrder";
import type { WhatsAppChatResponse } from "@/app/api/types/whatsapp";
import type { SMSChatResponse } from "@/app/api/types/sms";
import { resolveViewerSeat } from "@/app/chat/lib/viewerSeat";
import type { ExcelFilterItem } from "@/components/stocks/useExcelFilter";
import type { SourceSelection } from "../hooks/useChatPanelStack";

export interface SourceConfig {
  icon: LucideIcon;
  label: string;
  translationKey: string;
}

export const SOURCE_CONFIG: Record<number, SourceConfig> = {
  [PreOrderSourceType.EMAIL]: {
    icon: Mail,
    label: "Email",
    translationKey: "ordersRecap.filters.email",
  },
  [PreOrderSourceType.WHATSAPP]: {
    icon: MessageCircle,
    label: "WhatsApp",
    translationKey: "ordersRecap.filters.whatsapp",
  },
  [PreOrderSourceType.SMS]: {
    icon: Phone,
    label: "SMS",
    translationKey: "ordersRecap.filters.sms",
  },
  [PreOrderSourceType.MANUAL]: {
    icon: FileText,
    label: "Manuel",
    translationKey: "ordersRecap.filters.manual",
  },
  [PreOrderSourceType.INTERNAL_OFFER]: {
    icon: FileText,
    label: "Offre",
    translationKey: "ordersRecap.filters.internalOffer",
  },
  [PreOrderSourceType.ERP]: {
    icon: Database,
    label: "ERP",
    translationKey: "ordersRecap.filters.erp",
  },
  [PreOrderSourceType.AI_CHAT]: {
    icon: Bot,
    label: "Agent",
    translationKey: "ordersRecap.filters.aiChat",
  },
};

/** Display order of the source column filter — not the numeric enum order. */
export const SOURCE_FILTER_ORDER: readonly number[] = [
  PreOrderSourceType.EMAIL,
  PreOrderSourceType.WHATSAPP,
  PreOrderSourceType.SMS,
  PreOrderSourceType.MANUAL,
  PreOrderSourceType.INTERNAL_OFFER,
  PreOrderSourceType.ERP,
  PreOrderSourceType.AI_CHAT,
];

export interface PresentSourceTypesInput {
  /** Source types carried by the UNFILTERED loaded pre-orders. */
  preOrderSourceTypes: Iterable<number>;
  selectedSources: Iterable<number>;
  /** Persisted compound account ids, `"<sourceType>:<accountId>"`. */
  selectedAccountIds?: Iterable<string>;
}

/**
 * Source types the column filter should offer: the ones actually carried by
 * the visible orders, plus anything already selected. Selecting an account
 * child stores ONLY the compound id, so a parent type is also recovered from
 * the compound prefix — otherwise a persisted account-only filter would drop
 * its own parent out of the popover and become unreachable there.
 */
export function collectPresentSourceTypes({
  preOrderSourceTypes,
  selectedSources,
  selectedAccountIds,
}: PresentSourceTypesInput): Set<number> {
  const types = new Set<number>(selectedSources);
  for (const compoundId of selectedAccountIds ?? []) {
    const sourceType = Number(compoundId.slice(0, compoundId.indexOf(":")));
    if (!Number.isNaN(sourceType)) types.add(sourceType);
  }
  for (const sourceType of preOrderSourceTypes) types.add(sourceType);
  return types;
}

export interface SourceFilterItemsInput {
  /** Source types worth offering — the ones carried by the visible orders. */
  presentTypes: ReadonlySet<number>;
  /** Account sub-items for a type (empty when it has fewer than 2 accounts). */
  accountSubItems: (sourceType: number) => ExcelFilterItem[];
  translate: (translationKey: string) => string;
}

/**
 * Builds the source column filter items, offering only the channels the
 * viewer actually has orders on. A type with 2+ accounts becomes a parent
 * with one child per account.
 */
export function buildSourceFilterItems({
  presentTypes,
  accountSubItems,
  translate,
}: SourceFilterItemsInput): ExcelFilterItem[] {
  const items: ExcelFilterItem[] = [];
  for (const sourceType of SOURCE_FILTER_ORDER) {
    if (!presentTypes.has(sourceType)) continue;
    const id = String(sourceType);
    const name = translate(getSourceConfig(sourceType).translationKey);
    const subs = accountSubItems(sourceType);
    if (subs.length > 0) {
      items.push({ id, name, childIds: subs.map((s) => s.id) });
      for (const sub of subs)
        items.push({ ...sub, isChild: true, parentId: id });
    } else {
      items.push({ id, name });
    }
  }
  return items;
}

const DEFAULT_SOURCE = SOURCE_CONFIG[PreOrderSourceType.MANUAL];

export function getSourceConfig(sourceType: number): SourceConfig {
  return SOURCE_CONFIG[sourceType] || DEFAULT_SOURCE;
}

export interface DerivePreOrderSourceOptions {
  /** Viewer's user id — only the viewer themself can own a seat. */
  currentUserId?: string;
  /**
   * Viewer's full WhatsApp / SMS chat rows (legacy snake_case shape, the
   * output of `adaptOfflineWhatsappChat` / `adaptOfflineSmsChat`).
   *
   * When supplied, the pre-order's `whatsapp_chat_id` / `sms_chat_id` — which
   * carries the *trigger user's* seat for shared-group conversations — is
   * rewritten to the viewer's own seat for the same external conversation.
   * Without this rewrite, opening the chat panel from PartnerCell /
   * PreOrderSection / PreOrderFocusView would route outbound through a
   * teammate's WhatsApp instance, making the reply appear under their
   * identity on the actual conversation.
   *
   * Omitted by legacy callers / tests → no rewrite (pre-fix behaviour).
   */
  viewerWhatsappChats?: readonly WhatsAppChatResponse[];
  viewerSmsChats?: readonly SMSChatResponse[];
}

// Resolve the chat panel source (channel + chatId) from a pre-order.
// Mirrors ChatPanel.derivedConversation's fallback order: whatsapp → sms →
// email. Returns undefined for manual POs with no chat metadata. Callers
// pass the result to chatPanel.openPanel(poId, source) so the panel can
// render the conversation even when the PO isn't in the date-scoped recap
// data (cross-date navigation, filtered-out rows, etc.).
export function derivePreOrderSource(
  preOrder: PreOrder,
  options: DerivePreOrderSourceOptions = {},
): SourceSelection | undefined {
  const { currentUserId, viewerWhatsappChats, viewerSmsChats } = options;
  if (preOrder.whatsapp_chat_id) {
    const chatId = viewerWhatsappChats
      ? resolveViewerSeat(
          preOrder.whatsapp_chat_id,
          viewerWhatsappChats,
          (c) => c.jid,
          currentUserId,
        )
      : preOrder.whatsapp_chat_id;
    return { channel: "whatsapp", chatId };
  }
  if (preOrder.sms_chat_id) {
    const chatId = viewerSmsChats
      ? resolveViewerSeat(
          preOrder.sms_chat_id,
          viewerSmsChats,
          (c) => c.phone_number,
          currentUserId,
        )
      : preOrder.sms_chat_id;
    return { channel: "sms", chatId };
  }
  if (preOrder.email_id) {
    return { channel: "email", chatId: preOrder.email_id };
  }
  return undefined;
}
